/**
 * Opening a workspace file without leaving the chat: the viewer itself (also the
 * workspace panel's preview), the chip a file reference in the agent's text turns
 * into, and what those need from the thread (see fileRefs.ts for what counts as
 * a reference).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Download,
  Eye,
  File as FileIcon,
  FileCode2,
  Image as ImageIcon,
  MessageSquarePlus,
  Music,
  Video,
  X,
} from 'lucide-react';
import { api } from '@/services/api';
import { useWorkspaceFileSignal } from './useShellOutputStream';
import { openReviewer } from './tool-renderers/HtmlArtifactCard';
import { fileKind, fileName, resolveKnownPath, toWorkspaceRelPath, type FileKind } from './fileRefs';
import { formatSize } from './workspace-format';
import { downloadWorkspaceFile, useFileUrl } from './fileMedia';
import { MediaOverlay } from './MediaOverlay';

export interface ViewerFile {
  path: string;
  name?: string;
  size?: number;
  contentType?: string;
  /** What the agent called it, when it did. */
  caption?: string;
}

interface PreviewData {
  type: 'text' | 'binary';
  content?: string;
  contentType?: string;
  url?: string;
}


export function FileKindIcon({ kind, className }: { kind: FileKind; className?: string }) {
  const Icon = kind === 'image' ? ImageIcon : kind === 'video' ? Video : kind === 'audio' ? Music : kind === 'html' ? FileCode2 : FileIcon;
  return <Icon className={className} />;
}

// ── The viewer ──────────────────────────────────────────────────────────────

interface FileViewerProps {
  threadId: string;
  file: ViewerFile;
  onClose: () => void;
  onUseInChat?: () => void;
}

export const FileViewer: FC<FileViewerProps> = ({ threadId, file, onClose, onUseInChat }) => {
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const name = file.name ?? fileName(file.path);
  const kind = fileKind(name, file.contentType);
  const isPdf = /\.pdf$/i.test(name) || file.contentType === 'application/pdf';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    api.get<PreviewData>(`/chat/threads/${threadId}/workspace/preview`, { path: file.path })
      .catch(() =>
        api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path: file.path })
          .then(({ url }): PreviewData => ({ type: 'binary', url })))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [threadId, file.path]);

  // A missing file still gets a signed URL: only loading it shows it is gone.
  const gone = () => setData(null);

  // Focus stays here so Escape works: keys go to the workspace's VS Code once its
  // iframe has focus, and it takes focus by itself as it loads.
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onBlur = () => setTimeout(() => { if (document.activeElement?.tagName === 'IFRAME') dialog.current?.focus(); });
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100] bg-zinc-950/30 flex items-center justify-center p-6" onClick={onClose}>
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={name} className="bg-white rounded-lg border border-zinc-200 shadow-lg w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden outline-none" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="w-4 h-4 text-zinc-600 shrink-0" />
            <span className="text-sm font-semibold text-zinc-800 truncate" title={file.path}>{name}</span>
            {file.size != null && <span className="text-xs text-zinc-400 shrink-0">{formatSize(file.size)}</span>}
          </div>
          <div className="flex items-center gap-1.5">
            {onUseInChat && (
              <button onClick={onUseInChat} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-100 transition-colors" title="Use in chat">
                <MessageSquarePlus className="w-3.5 h-3.5" /> Chat
              </button>
            )}
            <button onClick={() => downloadWorkspaceFile(threadId, file.path)} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Download"><Download className="w-4 h-4 text-zinc-500" /></button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Close"><X className="w-4 h-4 text-zinc-500" /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" /></div>
          ) : data?.type === 'text' ? (
            <pre className="text-xs font-mono text-zinc-700 whitespace-pre-wrap break-words leading-relaxed bg-zinc-50 rounded-lg p-4 max-h-[65vh] overflow-auto">{data.content}</pre>
          ) : kind === 'image' && data?.url ? (
            <img src={data.url} alt={name} onError={gone} className="max-w-full max-h-[65vh] h-auto rounded-lg mx-auto shadow-sm" />
          ) : kind === 'video' && data?.url ? (
            <video src={data.url} controls autoPlay onError={gone} className="max-w-full max-h-[65vh] rounded-lg mx-auto shadow-sm" />
          ) : kind === 'audio' && data?.url ? (
            <audio src={data.url} controls autoPlay onError={gone} className="w-full mt-8" />
          ) : isPdf && data?.url ? (
            <iframe src={data.url} title={name} className="h-[70vh] w-full rounded-lg border border-zinc-200" />
          ) : data?.url ? (
            <div className="text-center py-12">
              <FileIcon className="w-12 h-12 text-zinc-200 mx-auto mb-3" />
              <p className="text-sm text-zinc-500 mb-3">Cannot preview this file type</p>
              <a href={data.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors">
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
          ) : (
            <div className="text-center py-12 text-sm text-zinc-400">This file is no longer in the workspace.</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ── What the chat needs from the thread ─────────────────────────────────────

interface ChatFiles {
  threadId: string | null;
  /** Opens the viewer; an HTML file goes to the reviewer, which is how one is meant to be seen. */
  openFile: (file: ViewerFile) => void;
  /** Several pictures or videos at once, as a collage. */
  openCollage: (files: ViewerFile[]) => void;
  /** Every file path in the workspace, once something has asked for them. */
  knownPaths: readonly string[];
  /** Ask for `knownPaths` (again): the listing is fetched only when text has a path to check. */
  requestKnownPaths: () => void;
}

const ChatFilesContext = createContext<ChatFiles>({
  threadId: null,
  openFile: () => {},
  openCollage: () => {},
  knownPaths: [],
  requestKnownPaths: () => {},
});

export const useChatFiles = () => useContext(ChatFilesContext);

/** Requests and file events arrive in bursts; one listing answers them all. */
const LISTING_DEBOUNCE_MS = 1_500;

export const ChatFilesProvider: FC<{ threadId: string | null; children: ReactNode }> = ({ threadId, children }) => {
  const [viewing, setViewing] = useState<ViewerFile | null>(null);
  const [media, setMedia] = useState<{ files: ViewerFile[]; start: number | null } | null>(null);
  const [knownPaths, setKnownPaths] = useState<readonly string[]>([]);
  const [requests, setRequests] = useState(0);
  const fileSignal = useWorkspaceFileSignal();

  const openFile = useCallback((file: ViewerFile) => {
    if (!threadId) return;
    const kind = fileKind(file.name ?? fileName(file.path), file.contentType);
    if (kind === 'html') openReviewer(threadId, file.path);
    else if (kind === 'image' || kind === 'video') setMedia({ files: [file], start: 0 });
    else setViewing(file);
  }, [threadId]);

  const openCollage = useCallback((files: ViewerFile[]) => setMedia({ files, start: null }), []);

  const requestKnownPaths = useCallback(() => setRequests((n) => n + 1), []);

  useEffect(() => {
    setViewing(null);
    setMedia(null);
    setKnownPaths([]);
  }, [threadId]);

  useEffect(() => {
    if (!threadId || requests === 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get<Array<{ path: string; isDirectory?: boolean }>>(`/chat/threads/${threadId}/workspace`)
        .then((files) => { if (!cancelled) setKnownPaths(files.filter((f) => !f.isDirectory).map((f) => f.path)); })
        .catch(() => {});
    }, LISTING_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [threadId, requests, fileSignal]);

  const value = useMemo(() => ({ threadId, openFile, openCollage, knownPaths, requestKnownPaths }), [threadId, openFile, openCollage, knownPaths, requestKnownPaths]);
  const close = useCallback(() => setViewing(null), []);
  const closeMedia = useCallback(() => setMedia(null), []);

  return (
    <ChatFilesContext.Provider value={value}>
      {children}
      {viewing && threadId && <FileViewer threadId={threadId} file={viewing} onClose={close} />}
      {media && threadId && <MediaOverlay threadId={threadId} files={media.files} start={media.start} onClose={closeMedia} />}
    </ChatFilesContext.Provider>
  );
};

// ── A file in the agent's text ──────────────────────────────────────────────

const CHIP =
  'inline-flex max-w-full items-center gap-1 rounded bg-accent-50 px-1 py-px align-baseline text-[0.92em] font-normal ' +
  'text-accent-700 transition-colors hover:bg-accent-100 cursor-pointer';

export function FileChip({ path, children }: { path: string; children?: ReactNode }) {
  const { openFile } = useChatFiles();
  return (
    <button type="button" onClick={() => openFile({ path })} title={path} className={CHIP}>
      <FileKindIcon kind={fileKind(path)} className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate">{children ?? fileName(path)}</span>
    </button>
  );
}

/** Inline code that may name a file (`rehypeFileRefs`): a chip when the workspace has it, otherwise the code it was. */
export function FileRefCode({ 'data-ref': ref, children }: { 'data-ref'?: string; children?: ReactNode }) {
  const { knownPaths, requestKnownPaths } = useChatFiles();
  const isLink = !!ref && /^ws:\/\//i.test(ref);
  useEffect(() => { if (!isLink) requestKnownPaths(); }, [isLink, requestKnownPaths]);

  const path = !ref ? null : isLink ? toWorkspaceRelPath(ref) : resolveKnownPath(ref, knownPaths);
  return path ? <FileChip path={path} /> : <code>{children}</code>;
}

/** `![alt](ws://a.png)` in the agent's text: the picture, opening full size. */
export function FileImage({ path, alt }: { path: string; alt?: string }) {
  const { threadId, openFile } = useChatFiles();
  const { ref, url, failed, onError } = useFileUrl(threadId, path);
  if (failed) return <FileChip path={path}>{alt || undefined}</FileChip>;
  return (
    <button ref={ref as any} type="button" onClick={() => openFile({ path })} title={path} className="my-1 block min-h-16 max-w-full cursor-zoom-in">
      {url && <img src={url} alt={alt ?? fileName(path)} onError={onError} className="max-h-40 max-w-[260px] rounded-md object-cover ring-1 ring-black/10" />}
    </button>
  );
}
